import type { CurrentReview } from "../../../../services/reviews.service";
import type { SubmitGuestReviewCardData } from "../../../types/model/submit-guest-review";
import {
  buildAvatarUrl,
  buildListingThumbnailUrl,
} from "../../../utils/cloudinary-image.utils";
import {
  MAX_SUBMIT_GUEST_REVIEW_CARDS,
  SUBMIT_GUEST_REVIEW_AVATAR_PX,
  SUBMIT_GUEST_REVIEW_THUMBNAIL_PX,
} from "./converter.consts";

const guestDisplayName = (review: CurrentReview): string | undefined => {
  const name = [review.guest?.name?.first, review.guest?.name?.last]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || undefined;
};

const toCard = (review: CurrentReview): SubmitGuestReviewCardData => ({
  guestAvatarUrl: buildAvatarUrl({
    avatarId: review.guest?.avatar_id,
    size: SUBMIT_GUEST_REVIEW_AVATAR_PX,
  }),
  guestName: guestDisplayName(review),
  listingPhotoUrl: buildListingThumbnailUrl({
    externalId: review.listing?.images?.[0]?.external_id,
    size: SUBMIT_GUEST_REVIEW_THUMBNAIL_PX,
  }),
  listingTitle: review.listing?.title,
  review,
  reviewId: review._id,
});

/**
 * Filters the pending reviews response to the ones a host still needs to write
 * about a guest (`reviewer_type: "host"`, `review_type: "guest"`, incomplete),
 * orders them alphabetically by listing title, and caps the result at
 * `MAX_SUBMIT_GUEST_REVIEW_CARDS` cards.
 */
export const toSubmitGuestReviewCards = (
  reviews: CurrentReview[] | undefined,
): SubmitGuestReviewCardData[] => {
  if (!reviews) return [];

  return reviews
    .filter(
      (review) =>
        "host" === review.reviewer_type &&
        "guest" === review.review_type &&
        !review.complete,
    )
    .sort((a, b) =>
      (a.listing?.title ?? "").localeCompare(b.listing?.title ?? ""),
    )
    .slice(0, MAX_SUBMIT_GUEST_REVIEW_CARDS)
    .map(toCard);
};
